import * as React from "react";
import styled from "styled-components";
import {
  Button,
  ButtonGroup,
  HTMLSelect,
  NumericInput,
  Text,
} from "@blueprintjs/core";
import { useDatatable2Context } from "../DataTable2";

export interface IPageSelectorProps {
  pageButtonCount?: number;
  showPageSizeSelector?: boolean;
}

const PaginatorContainer = styled.div`
  margin: 0.5em;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
`;

const Section = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  & > * + * {
    margin-left: 0.5em;
  }
`;

function range(start: number, end: number) {
  const arr = [];
  for (let i = start; i <= end; i++) {
    arr.push(i);
  }
  return arr;
}

export const PageSelector: React.FunctionComponent<IPageSelectorProps> = ({
  pageButtonCount = 3,
  showPageSizeSelector = true,
}) => {
  const {
    tableState: {
      pageSize,
      availablePageSizes,
      pageNumbers,
      canGoNextPage,
      canGoPreviousPage,
      goToPreviousPage,
      goToNextPage,
      goToPage,
      setPageSize,
      pageNumber,
      nbPages,
      totalCount,
    },
    translateFunc,
  } = useDatatable2Context();

  const distanceFromStart = React.useMemo(() => pageNumber - 1, [pageNumber]);
  const distanceFromEnd = React.useMemo(
    () => nbPages - pageNumber,
    [nbPages, pageNumber]
  );

  const visibleButtonPages = React.useMemo<number[]>(() => {
    if (!pageNumbers) return [];

    let startPageNumber =
      distanceFromEnd > pageButtonCount
        ? pageNumber
        : nbPages - pageButtonCount;
    if (startPageNumber > 1) {
      startPageNumber--;
    } else if (startPageNumber < 1) {
      startPageNumber = 1;
    }

    let endPageNumber = pageNumber + pageButtonCount;
    if (endPageNumber > nbPages) endPageNumber = nbPages > 0 ? nbPages : 1;
    return range(startPageNumber, endPageNumber);
  }, [distanceFromEnd, nbPages, pageButtonCount, pageNumber, pageNumbers]);

  const showEllipsisBefore = React.useMemo(
    () => distanceFromStart >= pageButtonCount - 1,
    [distanceFromStart, pageButtonCount]
  );

  const showEllipsisAfter = React.useMemo(
    () => distanceFromEnd >= pageButtonCount,
    [distanceFromEnd, pageButtonCount]
  );
  const minInputWidthRem = React.useMemo(() => {
    let lenght = nbPages.toString().length;
    if (lenght < 2) lenght = 2;
    return lenght + 0.5;
  }, [nbPages]);

  const goToPageProtected = React.useCallback(
    (page: number) => {
      let pageFinal = page;
      if (page < 1) {
        pageFinal = 1;
      } else if (page > nbPages) {
        pageFinal = nbPages;
      }
      goToPage(pageFinal);
    },
    [goToPage, nbPages]
  );

  const [inputPageNumber, setInputPageNumber] = React.useState(pageNumber);

  React.useEffect(() => {
    setInputPageNumber(pageNumber);
  }, [pageNumber]);

  if (totalCount === 0) {
    return null; // Do not render pager if no data present
  }

  return (
    <PaginatorContainer>
      <Section>
        {showPageSizeSelector ? (
          <>
            <HTMLSelect
              value={pageSize}
              onChange={(e) => {
                setPageSize(+e.target.value);
              }}
              minimal={false}
            >
              {availablePageSizes.map((e) => (
                <option key={e} value={e} style={{ textAlign: "center" }}>
                  {e}
                </option>
              ))}
            </HTMLSelect>
            <Text>{translateFunc(`Pager_Selector_Items_per_page`)}</Text>
          </>
        ) : null}
      </Section>
      <Section>
        <Text>{translateFunc(`Pager_Selector_Page`)}</Text>
        <NumericInput
          style={{ maxWidth: `${minInputWidthRem}rem`, textAlign: "center" }}
          buttonPosition="none"
          value={inputPageNumber}
          onValueChange={setInputPageNumber}
          min={1}
          max={nbPages}
          onKeyDown={(e) => {
            if (e.keyCode === 13) {
              goToPageProtected(+e.currentTarget.value);
              e.currentTarget.select();
            }
          }}
          onBlur={(e) => {
            goToPageProtected(+e.currentTarget.value);
          }}
          onClick={(e) => e.currentTarget.select()}
        />
        <Text>
          {translateFunc(`Pager_Selector_Of`)} {nbPages} ({totalCount}{" "}
          {translateFunc(`Pager_Selector_Items`)})
        </Text>
        <ButtonGroup minimal={true}>
          <Button icon="double-chevron-left" onClick={() => goToPage(1)} />
          <Button
            icon="chevron-left"
            disabled={!canGoPreviousPage}
            onClick={() => goToPreviousPage()}
          />
          {showEllipsisBefore && <Button disabled>...</Button>}
          {visibleButtonPages.map((number) => (
            <Button
              text={number}
              onClick={() => goToPage(number)}
              key={number}
              style={{
                textDecoration: pageNumber !== number ? "underline" : "none",
              }}
              intent={pageNumber === number ? "primary" : "none"}
            />
          ))}
          {showEllipsisAfter && <Button disabled> ...</Button>}
          <Button
            icon="chevron-right"
            disabled={!canGoNextPage}
            onClick={() => goToNextPage()}
          />
          <Button
            icon="double-chevron-right"
            onClick={() => goToPage(nbPages)}
          />
        </ButtonGroup>
      </Section>
    </PaginatorContainer>
  );
};
